import { type FC, memo } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import {
  hasNameColumn,
  LAST_ORDER_FORMAT,
  type AddRowValues,
  type ListColumnMap,
} from "./audienceListRows";

const NUMBER_PATTERN = /^\d+(\.\d+)?$/;

interface AudienceAddRowFormProps {
  listName: string;
  columns: ListColumnMap;
  onAdd: (_values: AddRowValues) => void;
  onCancel: () => void;
}

const AudienceAddRowForm: FC<AudienceAddRowFormProps> = ({ listName, columns, onAdd, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AddRowValues>();
  const label = (key: string) => t(`audienceOverview.listModal.${key}`);
  const numberRule = { pattern: NUMBER_PATTERN, message: label("numberInvalid") };

  const handleFinish = (values: AddRowValues) => {
    onAdd(values);
    form.resetFields();
  };

  return (
    <div className="audience-list-modal__add-row">
      <p className="audience-list-modal__add-row-title">{label("addRowLink")}</p>
      <p className="audience-list-modal__add-row-hint">
        {t("audienceOverview.listModal.addRowHint", { name: listName })}
      </p>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
        className="audience-list-modal__form"
      >
        <div className="audience-list-modal__form-grid">
          {hasNameColumn(columns) && (
            <Form.Item
              name="name"
              label={label("nameLabel")}
              rules={[{ required: true, whitespace: true, message: label("nameRequired") }]}
            >
              <StyledTextInput placeholder={label("namePlaceholder")} autoFocus />
            </Form.Item>
          )}
          {columns.email && (
            <Form.Item
              name="email"
              label={label("emailLabel")}
              rules={[
                { required: true, whitespace: true, message: label("emailRequired") },
                { type: "email", message: label("emailInvalid") },
              ]}
            >
              <StyledTextInput placeholder={label("emailPlaceholder")} />
            </Form.Item>
          )}
          {columns.phone && (
            <Form.Item name="phone" label={label("phoneLabel")}>
              <StyledTextInput placeholder={label("phonePlaceholder")} />
            </Form.Item>
          )}
          {columns.totalOrders && (
            <Form.Item name="totalOrders" label={label("totalOrders")} rules={[numberRule]}>
              <StyledTextInput placeholder={label("totalOrdersPlaceholder")} inputMode="numeric" />
            </Form.Item>
          )}
          {columns.totalSpent && (
            <Form.Item name="totalSpent" label={label("totalSpent")} rules={[numberRule]}>
              <StyledTextInput placeholder={label("totalSpentPlaceholder")} inputMode="decimal" />
            </Form.Item>
          )}
          {columns.lastOrder && (
            <Form.Item
              name="lastOrder"
              label={label("lastOrderLabel")}
              rules={[
                {
                  validator: (_, value?: string) =>
                    !value || dayjs(value, LAST_ORDER_FORMAT, true).isValid()
                      ? Promise.resolve()
                      : Promise.reject(new Error(label("dateInvalid"))),
                },
              ]}
            >
              <StyledTextInput placeholder={LAST_ORDER_FORMAT} />
            </Form.Item>
          )}
        </div>
        <div className="audience-list-modal__form-actions">
          <button type="button" className="audience-list-modal__secondary-btn" onClick={onCancel}>
            {label("cancel")}
          </button>
          <button type="submit" className="button button--primary">
            {label("addRow")}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default memo(AudienceAddRowForm);
